/**
 * One message row (the prototype's row markup in `messageList()`): optional thumbnail, the folder icon
 * with its name as tooltip (fix 5), the one-line title (bold when unread), three preview lines, source
 * and time; then the chip row — the icons of the filters that matched (fix 4) BEFORE the topic chips,
 * a dash for a hand-assigned unscored topic, the scoring state when there are no topics (naming the model it
 * waits for), the unread dot. A chip's tooltip names the model whose score it shows ("AI · 78 / 100 · Jev").
 * Numbers are content-sized (fix 2).
 */

import { memo } from 'react';
import type { MessageRow as Row } from '@digester/core';
import { Icon } from '../icons/Icon.js';
import { FolderIcon } from '../ui/FolderIcon.js';
import { sourceKindIcon } from '../ui/source-icons.js';
import type { Lookups } from './data.js';
import { rowTime, headingOf } from './format.js';

const SCORE_STATE_LABEL: Partial<Record<Row['scoreState'], string>> = {
  pending: 'Scoring pending',
  skipped: 'Scoring skipped',
  failed: 'Scoring failed',
};

export interface MessageRowProps {
  row: Row;
  selected: boolean;
  lookups: Lookups;
  showImages: boolean;
  timeZone: string | undefined;
  /** Rows of messages that no longer exist (Corrections) cannot be opened. */
  gone?: boolean;
  onOpen(id: Row['id']): void;
}

export const MessageRowView = memo(function MessageRowView({ row, selected, lookups, showImages, timeZone, gone, onOpen }: MessageRowProps) {
  const source = lookups.source(row.sourceId);
  const folder = lookups.folder(row.folderId);
  const heading = headingOf(row);
  const waiting = row.waitingFor?.[0];
  const pendingLabel =
    row.topics.length === 0 ? (row.scoreState === 'pending' && waiting ? `Waiting for ${lookups.onDevice(waiting) ? 'the on-device model' : lookups.modelShort(waiting)}` : SCORE_STATE_LABEL[row.scoreState]) : undefined;
  const by = row.scoredBy ? lookups.modelShort(row.scoredBy) : '';
  return (
    <article className={`message-row${selected ? ' selected' : ''}${row.read ? ' read' : ''}`} data-id={row.id}>
      <button type="button" className="message-hit" aria-label={heading} aria-current={selected ? 'true' : undefined} disabled={gone} onClick={() => onOpen(row.id)}>
        <span className="message-main">
          {row.imageUrl && showImages ? <img className="message-image" src={row.imageUrl} alt="" width={76} height={76} loading="lazy" decoding="async" referrerPolicy="no-referrer" /> : null}
          <span className="message-copy">
            <span className="message-title">
              <FolderIcon folder={folder} />
              <strong>{heading}</strong>
            </span>
            <span className="message-excerpt">{row.excerpt}</span>
            <span className="message-source">
              <Icon name={sourceKindIcon(source?.kind ?? 'rss')} />
              <span>{source?.name ?? ''}</span>
              <time dateTime={new Date(row.sortAt).toISOString()}>{rowTime(row.sortAt, timeZone)}</time>
            </span>
          </span>
        </span>
        <span className="message-topics">
          {row.matchedFilterIds.map((id) => {
            const rule = lookups.rule(id);
            const name = rule?.name ?? 'Filter';
            return (
              <span key={id} className="filter-mark" title={`Matched filter · ${name}`} aria-label={`Matched filter ${name}`} role="img" data-filter={id}>
                <Icon name={rule?.icon ?? 'list-filter'} />
              </span>
            );
          })}
          {row.topics.map((t) => {
            const topic = lookups.topic(t.topicId);
            if (!topic) return null;
            const scored = t.score !== null;
            return (
              <span
                key={t.topicId}
                className="topic-chip"
                style={{ ['--topic' as string]: topic.color }}
                title={`${topic.name} · ${scored ? `${t.score} / 100${by ? ` · ${by}` : ''}` : 'Manually assigned'}`}
                aria-label={`${topic.name} ${scored ? `${t.score} out of 100${by ? `, scored by ${by}` : ''}` : 'manually assigned'}`}
                data-topic={t.topicId}
              >
                <Icon name={topic.icon} />
                <b>{scored ? t.score : '—'}</b>
              </span>
            );
          })}
          {pendingLabel ? <span className="pending-score">{pendingLabel}</span> : null}
          {!row.read ? <i className="unread-dot" aria-label="Unread" /> : null}
        </span>
      </button>
    </article>
  );
});
